// _forms/_shared/hooks/useFieldHandlers.js
import { useState } from 'react';

export const useFieldHandlers = (initialFields) => {
  const [deletedFields, setDeletedFields] = useState([]);

  // ==========================================
  // 1. CAMBIO DE TIPO DINÁMICO (MIGRACIÓN DE FORMULARIOS)
  // ==========================================
  const handleTypeChangeWrapper = (fieldId, newTypeConfig, setFormConfig) => {
    setFormConfig((prevConfig) => ({
      ...prevConfig,
      fields: {
        ...prevConfig.fields,
        fields: prevConfig.fields.fields.map((field) => {
          if (field.id === fieldId) {
            let newOptions = field.options;
            let newCategoryRules = field.categoryRules;
            let newFieldConditions = field.fieldConditions || [];

            if (newTypeConfig.type === 'select') {
              newOptions =
                field.options && field.options.length > 0
                  ? field.options
                  : [{ label: 'Opción 1', conditions: [], conditionOperator: 'OR' }];
            } else {
              newOptions = [];
            }

            if (newTypeConfig.type === 'category') {
              newCategoryRules = field.categoryRules || [];
            } else {
              newCategoryRules = [];
            }

            if (newTypeConfig.type === 'date') {
              newFieldConditions = [];
            }

            return {
              ...field,
              type: newTypeConfig.type,
              dataType: newTypeConfig.dataType || 'text',
              options: newOptions,
              categoryRules: newCategoryRules,
              fieldConditions: newFieldConditions,
              value:
                newTypeConfig.type === 'date' || newTypeConfig.type === 'category'
                  ? ''
                  : field.value,
              invalid: false,
              errorMessage: '',
            };
          }
          return field;
        }),
      },
    }));
  };

  // ==========================================
  // 2. MANEJO DE VALORES (SIN VALIDACIÓN EN CADA LETRA)
  // ==========================================
  const handleFieldChangeWrapper = (e, fieldId, setFormConfig) => {
    const { value } = e.target;

    setFormConfig((prevConfig) => ({
      ...prevConfig,
      fields: {
        ...prevConfig.fields,
        fields: prevConfig.fields.fields.map((field) =>
          field.id === fieldId
            ? {
                ...field,
                value: value || '',
                // ✅ NO se toca invalid ni errorMessage aquí
                // La validación se hará al presionar "Siguiente" en el componente padre
              }
            : field
        ),
      },
    }));
  };

  const handleLabelChangeWrapper = (e, fieldId, setFormConfig) => {
    setFormConfig((prevConfig) => ({
      ...prevConfig,
      fields: {
        ...prevConfig.fields,
        fields: prevConfig.fields.fields.map((field) =>
          field.id === fieldId ? { ...field, label: e.target.value } : field
        ),
      },
    }));
  };

  // ==========================================
  // 3. GESTIÓN DE CAMPOS (CREAR / ELIMINAR)
  // ==========================================
  const handleAddFieldWrapper = (type, dataType = 'text', setFormConfig) => {
    setFormConfig((prevConfig) => {
      const fields = prevConfig.fields.fields || [];
      const maxId = fields.reduce(
        (max, f) => (typeof f.id === 'number' && f.id > max ? f.id : max),
        0
      );
      const newId = maxId + 1;

      const newField = {
        id: newId,
        type,
        dataType,
        label:
          type === 'date'
            ? 'Fecha de Nacimiento'
            : type === 'category'
            ? 'Categoría'
            : `Nuevo Campo ${fields.length + 1}`,
        value: type === 'banner' ? 'Escribe aquí la información o instrucción para el usuario...' : '',
        options:
          type === 'select'
            ? [
                { label: 'Opción 1', conditions: [], conditionOperator: 'OR' },
                { label: 'Opción 2', conditions: [], conditionOperator: 'OR' },
              ]
            : [],
        categoryRules: type === 'category' ? [] : undefined,
        conditions: [],
        required: false,
        invalid: false,
        errorMessage: '',
      };

      return {
        ...prevConfig,
        fields: {
          ...prevConfig.fields,
          fields: [...fields, newField],
        },
      };
    });
  };

  const handleDeleteFieldWrapper = (fieldId, setFormConfig, setDeletedFields) => {
    setFormConfig((prevConfig) => ({
      ...prevConfig,
      fields: {
        ...prevConfig.fields,
        fields: prevConfig.fields.fields.filter((field) => field.id !== fieldId),
      },
    }));
    setDeletedFields((prev) => [...prev, fieldId]);
  };

  // ==========================================
  // 4. GESTIÓN DE REGLAS DE CATEGORÍA
  // ==========================================
  const handleAddCategoryRuleWrapper = (fieldId, setFormConfig) => {
    setFormConfig((prevConfig) => ({
      ...prevConfig,
      fields: {
        ...prevConfig.fields,
        fields: prevConfig.fields.fields.map((field) => {
          if (field.id === fieldId && field.type === 'category') {
            const currentRules = field.categoryRules || [];
            const newRule = {
              // 🟢 NUEVO: ahora una regla puede aplicar a VARIOS paquetes.
              // Se mantiene "packageId" solo por compatibilidad con reglas viejas.
              packageIds: [],
              label: 'Nueva Categoría',
              minAge: 0,
              maxAge: 99,
              isSpecial: false,
              // 🟢 NUEVO: condiciones extra (ej: campo "Tipo" = "Competitivo")
              conditions: [],
              conditionOperator: 'OR',
            };
            return { ...field, categoryRules: [...currentRules, newRule] };
          }
          return field;
        }),
      },
    }));
  };

  const handleUpdateCategoryRuleWrapper = (fieldId, ruleIndex, key, value, setFormConfig) => {
    setFormConfig((prevConfig) => ({
      ...prevConfig,
      fields: {
        ...prevConfig.fields,
        fields: prevConfig.fields.fields.map((field) => {
          if (field.id === fieldId && field.type === 'category') {
            const rules = [...(field.categoryRules || [])];
            rules[ruleIndex] = { ...rules[ruleIndex], [key]: value };
            return { ...field, categoryRules: rules };
          }
          return field;
        }),
      },
    }));
  };

  const handleDeleteCategoryRuleWrapper = (fieldId, ruleIndex, setFormConfig) => {
    setFormConfig((prevConfig) => ({
      ...prevConfig,
      fields: {
        ...prevConfig.fields,
        fields: prevConfig.fields.fields.map((field) => {
          if (field.id === fieldId && field.type === 'category') {
            const rules = [...(field.categoryRules || [])];
            rules.splice(ruleIndex, 1);
            return { ...field, categoryRules: rules };
          }
          return field;
        }),
      },
    }));
  };

  // 🟢 NUEVO: marcar/desmarcar un paquete dentro de una regla de categoría
  // (permite que "Master A" aplique al mismo tiempo a 30K y 17K, por ejemplo)
  const handleToggleCategoryRulePackageWrapper = (
    fieldId,
    ruleIndex,
    packageId,
    setFormConfig
  ) => {
    setFormConfig((prevConfig) => ({
      ...prevConfig,
      fields: {
        ...prevConfig.fields,
        fields: prevConfig.fields.fields.map((field) => {
          if (field.id === fieldId && field.type === 'category') {
            const rules = [...(field.categoryRules || [])];
            const rule = rules[ruleIndex];
            if (!rule) return field;

            // Migrar reglas viejas (packageId único) al nuevo formato la primera vez que se tocan
            const current =
              rule.packageIds && rule.packageIds.length > 0
                ? rule.packageIds
                : rule.packageId
                ? [rule.packageId]
                : [];

            const idStr = String(packageId);
            const already = current.map(String).includes(idStr);
            const nextPackageIds = already
              ? current.filter((id) => String(id) !== idStr)
              : [...current, packageId];

            rules[ruleIndex] = {
              ...rule,
              packageIds: nextPackageIds,
              packageId: undefined, // ya no se usa, todo vive en packageIds
            };
            return { ...field, categoryRules: rules };
          }
          return field;
        }),
      },
    }));
  };

  // 🟢 NUEVO: guardar condiciones extra de una regla de categoría
  // (ej: "solo si el campo Tipo = Competitivo"), reutilizando el mismo
  // formato de condiciones que ya usan los campos y las opciones de select.
  const handleUpdateCategoryRuleConditionsWrapper = (
    fieldId,
    ruleIndex,
    conditions,
    operator,
    setFormConfig
  ) => {
    setFormConfig((prevConfig) => ({
      ...prevConfig,
      fields: {
        ...prevConfig.fields,
        fields: prevConfig.fields.fields.map((field) => {
          if (field.id === fieldId && field.type === 'category') {
            const rules = [...(field.categoryRules || [])];
            if (!rules[ruleIndex]) return field;
            rules[ruleIndex] = {
              ...rules[ruleIndex],
              conditions,
              conditionOperator: operator || 'OR',
            };
            return { ...field, categoryRules: rules };
          }
          return field;
        }),
      },
    }));
  };

  // ==========================================
  // 5. GESTIÓN DE OPCIONES DE SELECT
  // ==========================================
  const handleAddOptionWrapper = (fieldId, setFormConfig) => {
    setFormConfig((prevConfig) => ({
      ...prevConfig,
      fields: {
        ...prevConfig.fields,
        fields: prevConfig.fields.fields.map((field) => {
          if (field.id === fieldId && field.type === 'select') {
            const newOption = {
              label: `Opción ${field.options.length + 1}`,
              conditions: [],
              conditionOperator: 'OR',
            };
            return { ...field, options: [...field.options, newOption] };
          }
          return field;
        }),
      },
    }));
  };

  const handleDeleteOptionWrapper = (fieldId, optionIndex, setFormConfig) => {
    setFormConfig((prevConfig) => ({
      ...prevConfig,
      fields: {
        ...prevConfig.fields,
        fields: prevConfig.fields.fields.map((field) => {
          if (field.id === fieldId && field.type === 'select') {
            const newOptions = [...field.options];
            newOptions.splice(optionIndex, 1);
            return { ...field, options: newOptions };
          }
          return field;
        }),
      },
    }));
  };

  const handleOptionChangeWrapper = (fieldId, optionIndex, newValue, setFormConfig) => {
    setFormConfig((prevConfig) => ({
      ...prevConfig,
      fields: {
        ...prevConfig.fields,
        fields: prevConfig.fields.fields.map((field) => {
          if (field.id === fieldId && field.type === 'select') {
            const newOptions = field.options.map((opt, i) => {
              if (i !== optionIndex) return opt;
              if (typeof opt === 'string') {
                return { label: newValue, conditions: [], conditionOperator: 'OR' };
              }
              return { ...opt, label: newValue };
            });
            return { ...field, options: newOptions };
          }
          return field;
        }),
      },
    }));
  };

  const handleOptionConditionsWrapper = (fieldId, optionIndex, conditions, operator, setFormConfig) => {
    setFormConfig((prevConfig) => ({
      ...prevConfig,
      fields: {
        ...prevConfig.fields,
        fields: prevConfig.fields.fields.map((field) => {
          if (field.id === fieldId && field.type === 'select') {
            const newOptions = field.options.map((opt, i) => {
              if (i !== optionIndex) return opt;
              const base = typeof opt === 'string' ? { label: opt } : { ...opt };
              return { ...base, conditions, conditionOperator: operator };
            });
            return { ...field, options: newOptions };
          }
          return field;
        }),
      },
    }));
  };

  // ==========================================
  // 6. OTROS
  // ==========================================
  const handleRequiredToggleWrapper = (fieldId, setFormConfig) => {
    setFormConfig((prevConfig) => ({
      ...prevConfig,
      fields: {
        ...prevConfig.fields,
        fields: prevConfig.fields.fields.map((field) =>
          field.id === fieldId ? { ...field, required: !field.required } : field
        ),
      },
    }));
  };

  // ==========================================
// 7. REORDENAR CAMPOS (Drag & Drop)
// ==========================================
const handleReorderField = (dragIndex, hoverIndex, setFormConfig) => {
  if (dragIndex === hoverIndex) return;
  
  setFormConfig((prevConfig) => {
    const fields = [...prevConfig.fields.fields];
    const [draggedField] = fields.splice(dragIndex, 1);
    fields.splice(hoverIndex, 0, draggedField);
    
    return {
      ...prevConfig,
      fields: {
        ...prevConfig.fields,
        fields,
      },
    };
  });
};

const handleMoveFieldUp = (index, setFormConfig) => {
  if (index === 0) return;
  handleReorderField(index, index - 1, setFormConfig);
};

const handleMoveFieldDown = (index, setFormConfig) => {
  setFormConfig((prevConfig) => {
    const fields = [...prevConfig.fields.fields];
    if (index >= fields.length - 1) return prevConfig;
    
    const [field] = fields.splice(index, 1);
    fields.splice(index + 1, 0, field);
    
    return {
      ...prevConfig,
      fields: {
        ...prevConfig.fields,
        fields,
      },
    };
  });
};

  return {
    deletedFields,
    setDeletedFields,
    handleTypeChangeWrapper,
    handleAddCategoryRuleWrapper,
    handleUpdateCategoryRuleWrapper,
    handleDeleteCategoryRuleWrapper,
    handleToggleCategoryRulePackageWrapper,
    handleUpdateCategoryRuleConditionsWrapper,
    handleFieldChangeWrapper,
    handleLabelChangeWrapper,
    handleAddFieldWrapper,
    handleDeleteFieldWrapper,
    handleAddOptionWrapper,
    handleDeleteOptionWrapper,
    handleOptionChangeWrapper,
    handleOptionConditionsWrapper,
    handleRequiredToggleWrapper,
    handleReorderField,
    handleMoveFieldUp,
    handleMoveFieldDown,
  };
};